import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { useSnackbar } from 'notistack';
import '../../utils/quillBlots';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { sendNewsletter, clearSendSuccess } from '../../store/slices/sentEmailSlice';
import * as sentEmailApi from '../../api/sentEmailApi';
import { FACULTY_OPTIONS } from '../../utils/formationConstants';
import { getCountries } from '../../utils/countries';
import { getEmbedUrl } from '../../utils/youtubeEmbed';

const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image', 'video', 'align', 'blockquote', 'color', 'background'];

export default function SendNewsletter() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.auth);
  const { loading } = useSelector((s) => s.sentEmail);
  const quillRef = useRef(null);
  const headerInputRef = useRef(null);
  const pendingInlineRef = useRef(new Map());
  const headlineBlobUrlRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    body: '',
    headlineImage: null,
    targetAudience: 'alumni',
    filters: { country: '', faculty: '' },
  });
  const [headlineFile, setHeadlineFile] = useState(null);
  const [headlinePreviewUrl, setHeadlinePreviewUrl] = useState(null);
  const [countries, setCountries] = useState([]);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image', 'video'],
          [{ align: [] }],
          ['blockquote'],
          [{ color: [] }, { background: [] }],
          ['clean'],
        ],
        handlers: {
          image: function imageHandler() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) return;
              const blobUrl = URL.createObjectURL(file);
              pendingInlineRef.current.set(blobUrl, { file, type: 'image' });
              const quill = quillRef.current?.getEditor?.();
              if (quill) {
                const range = quill.getSelection(true) || { index: quill.getLength() };
                quill.insertEmbed(range.index, 'image', blobUrl);
                quill.setSelection(range.index + 1);
              }
            };
          },
          video: function videoHandler() {
            setEmbedUrl('');
            setEmbedDialogOpen(true);
          },
        },
      },
    }),
    []
  );

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  const handleEmbedVideo = useCallback(() => {
    const embedSrc = getEmbedUrl(embedUrl);
    if (embedSrc) {
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, 'video', embedSrc);
        quill.setSelection(range.index + 1);
      }
      setEmbedDialogOpen(false);
      setEmbedUrl('');
      enqueueSnackbar('Vidéo intégrée.', { variant: 'success' });
    } else {
      enqueueSnackbar('URL non valide. Utilisez un lien YouTube ou Vimeo.', { variant: 'error' });
    }
  }, [embedUrl, enqueueSnackbar]);

  if (user?.role !== 'admin' && user?.role !== 'moderator') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleHeaderMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (headlineBlobUrlRef.current) URL.revokeObjectURL(headlineBlobUrlRef.current);
    const blobUrl = URL.createObjectURL(file);
    headlineBlobUrlRef.current = blobUrl;
    setHeadlinePreviewUrl(blobUrl);
    setHeadlineFile(file);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const handleRemoveHeaderMedia = () => {
    if (headlineBlobUrlRef.current) {
      URL.revokeObjectURL(headlineBlobUrlRef.current);
      headlineBlobUrlRef.current = null;
    }
    setHeadlinePreviewUrl(null);
    setHeadlineFile(null);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const uploadAllPendingMedia = async (bodyHtml) => {
    const pending = pendingInlineRef.current;
    const blobUrls = Array.from(pending.keys()).filter((url) => bodyHtml.includes(url));
    let finalHeadline = form.headlineImage;
    if (blobUrls.length === 0 && !headlineFile) return { body: bodyHtml, headlineImage: finalHeadline };

    let newBody = bodyHtml;
    const total = blobUrls.length + (headlineFile ? 1 : 0);
    let done = 0;
    const updateProgress = (phase, percent) => setUploadStatus({ phase, current: done, total, percent });

    if (headlineFile) {
      updateProgress('headline', 0);
      const result = await sentEmailApi.uploadNewsletterMedia(
        headlineFile,
        'headline',
        (p) => updateProgress('headline', p)
      );
      done++;
      finalHeadline = { url: result.url, publicId: result.publicId, resourceType: result.resourceType || 'image' };
      setHeadlineFile(null);
      if (headlineBlobUrlRef.current) {
        URL.revokeObjectURL(headlineBlobUrlRef.current);
        headlineBlobUrlRef.current = null;
      }
      setHeadlinePreviewUrl(null);
    }

    for (let i = 0; i < blobUrls.length; i++) {
      const blobUrl = blobUrls[i];
      const entry = pending.get(blobUrl);
      if (!entry?.file) continue;
      const completedCount = done;
      updateProgress('inline', 0);
      const result = await sentEmailApi.uploadNewsletterMedia(entry.file, 'inline', (p) =>
        setUploadStatus({ phase: 'inline', current: completedCount, total, percent: p })
      );
      const escaped = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      newBody = newBody.replace(new RegExp(escaped, 'g'), result.url);
      pending.delete(blobUrl);
      URL.revokeObjectURL(blobUrl);
      done++;
    }
    return { body: newBody, headlineImage: finalHeadline };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadStatus({ phase: 'preparing', current: 0, total: 1, percent: 0 });
    try {
      const { body: finalBody, headlineImage: finalHeadline } = await uploadAllPendingMedia(form.body);
      setUploadStatus(null);
      const filters = {};
      if (form.filters.country) filters.country = form.filters.country;
      if ((form.targetAudience === 'alumni' || form.targetAudience === 'both') && form.filters.faculty)
        filters.faculty = form.filters.faculty;
      const payload = {
        title: form.title,
        body: finalBody,
        targetAudience: form.targetAudience,
        ...(Object.keys(filters).length ? { filters } : {}),
        ...(finalHeadline && { headlineImage: finalHeadline }),
      };
      const result = await dispatch(sendNewsletter(payload));
      if (sendNewsletter.fulfilled.match(result)) {
        enqueueSnackbar('Newsletter envoyée avec succès.', { variant: 'success' });
        dispatch(clearSendSuccess());
        navigate('/dashboard/alumni/history');
      } else {
        enqueueSnackbar(result.payload || 'Erreur lors de l\'envoi.', { variant: 'error' });
      }
    } catch (err) {
      setUploadStatus(null);
      enqueueSnackbar(err?.response?.data?.message || 'Erreur lors de l\'envoi.', { variant: 'error' });
    }
  };

  const headerPreviewUrl = headlinePreviewUrl || form.headlineImage?.url;

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
        <IconButton onClick={() => navigate(-1)} disabled={!!uploadStatus} aria-label="Retour">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Envoyer un e-mail
        </Typography>
      </Box>

      {uploadStatus && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadStatus.percent} sx={{ height: 6, borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {uploadStatus.phase === 'headline' && 'Upload image d\'en-tête…'}
            {uploadStatus.phase === 'inline' && 'Upload images…'}
            {uploadStatus.phase === 'preparing' && 'Préparation…'}
          </Typography>
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Titre"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />

            <FormControl component="fieldset">
              <FormLabel>Cible</FormLabel>
              <RadioGroup row value={form.targetAudience} onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value }))}>
                <FormControlLabel value="alumni" control={<Radio />} label="Alumni" />
                <FormControlLabel value="newsletter" control={<Radio />} label="Newsletter" />
                <FormControlLabel value="both" control={<Radio />} label="Alumni & Newsletter" />
              </RadioGroup>
            </FormControl>

            {(form.targetAudience === 'alumni' || form.targetAudience === 'both') && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  label="Pays (filtre)"
                  value={form.filters.country}
                  onChange={(e) => setForm((f) => ({ ...f, filters: { ...f.filters, country: e.target.value } }))}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Faculté (filtre)"
                  value={form.filters.faculty}
                  onChange={(e) => setForm((f) => ({ ...f, filters: { ...f.filters, faculty: e.target.value } }))}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {FACULTY_OPTIONS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            {form.targetAudience === 'newsletter' && (
              <TextField
                select
                size="small"
                label="Pays (filtre)"
                value={form.filters.country}
                onChange={(e) => setForm((f) => ({ ...f, filters: { ...f.filters, country: e.target.value } }))}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">Tous</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Image d'en-tête (optionnel)
              </Typography>
              <input ref={headerInputRef} type="file" accept="image/*" onChange={handleHeaderMediaChange} style={{ display: 'none' }} />
              {headerPreviewUrl ? (
                <Box sx={{ position: 'relative', maxWidth: 500 }}>
                  <Box component="img" src={headerPreviewUrl} alt="En-tête" sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 2, display: 'block' }} />
                  <IconButton
                    size="small"
                    onClick={handleRemoveHeaderMedia}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => headerInputRef.current?.click()} size="small">
                  Choisir une image
                </Button>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Contenu
              </Typography>
              <Box sx={{ '& .quill': { bgcolor: 'background.paper' }, '& .ql-editor': { minHeight: 200 } }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.body}
                  onChange={(v) => setForm((f) => ({ ...f, body: v }))}
                  modules={modules}
                  formats={formats}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !form.title || !form.body || !!uploadStatus}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Envoi…' : 'Envoyer'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      <Dialog open={embedDialogOpen} onClose={() => setEmbedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Intégrer une vidéo YouTube / Vimeo</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="URL de la vidéo"
            placeholder="https://www.youtube.com/watch?v=..."
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleEmbedVideo} startIcon={<YouTubeIcon />}>
            Insérer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
